import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AXES,
  JETON_REFERENCE,
  LANGUES_ANNONCE,
  LIBELLES_AXES,
  LIBELLES_LANGUE,
  LIBELLES_PLATEFORME,
  LIBELLES_STATUT_ANNONCE,
  PLATEFORMES,
  TYPES_CONTRAT,
  auMoins,
  proposerTexte,
  verifierValidation,
  type AnnonceDetail,
  type Axe,
  type CritereAnnonce,
  type EnregistrementAnnonce,
  type LangueAnnonce,
  type Plateforme,
  type Publication,
  type SaisieAnnonce,
  type TexteVersion,
  type TextesAnnonce,
  type TypeContrat,
} from '@rh/shared';
import { appel } from '../api';
import { useAuth } from '../auth/contexte-auth';
import { Chargement, Erreur } from '../composants/Etat';
import { dateHeure } from '../format';

interface Referentiel {
  id: string;
  libelle: string;
}

const SAISIE_VIDE: SaisieAnnonce = {
  familleId: '',
  intitule: '',
  pointsDeVente: [],
  typeContrat: 'CDI',
  horaires: null,
  remuneration: null,
  datePriseDePoste: null,
  missions: [''],
  criteres: [],
  langues: ['fr'],
};

/** Retire les lignes laissées vides par le formulaire avant l'envoi. */
function nettoyer(saisie: SaisieAnnonce): SaisieAnnonce {
  const texte = (valeur: string | null) => (valeur?.trim() ? valeur.trim() : null);
  return {
    ...saisie,
    intitule: saisie.intitule.trim(),
    horaires: texte(saisie.horaires),
    remuneration: texte(saisie.remuneration),
    datePriseDePoste: saisie.datePriseDePoste || null,
    missions: saisie.missions.map((m) => m.trim()).filter(Boolean),
    criteres: saisie.criteres
      .map((c) => ({ ...c, libelle: c.libelle.trim() }))
      .filter((c) => c.libelle),
  };
}

/**
 * Création et suivi d'une annonce (docs/ARCHITECTURE.md § 5.1) : formulaire
 * guidé, proposition de texte, édition libre, validation, publications.
 */
export function EditionAnnonce() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { utilisateur } = useAuth();
  const [detail, setDetail] = useState<AnnonceDetail | null>(null);
  const [familles, setFamilles] = useState<Referentiel[]>([]);
  const [lieux, setLieux] = useState<Referentiel[]>([]);
  const [saisie, setSaisie] = useState<SaisieAnnonce | null>(id ? null : SAISIE_VIDE);
  const [textes, setTextes] = useState<TextesAnnonce>({});
  const [modifie, setModifie] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [versionLue, setVersionLue] = useState<TexteVersion | null>(null);

  const charger = useCallback((lu: AnnonceDetail) => {
    setDetail(lu);
    setSaisie({ ...lu.saisie, missions: lu.saisie.missions.length > 0 ? lu.saisie.missions : [''] });
    setTextes(lu.textes);
    setModifie(false);
  }, []);

  useEffect(() => {
    appel<Referentiel[]>('/familles').then(setFamilles).catch(() => setFamilles([]));
    appel<Referentiel[]>('/points-de-vente').then(setLieux).catch(() => setLieux([]));
  }, []);

  useEffect(() => {
    if (!id) return;
    appel<AnnonceDetail>(`/annonces/${id}`)
      .then(charger)
      .catch((e: Error) => setErreur(e.message));
  }, [id, charger]);

  const aVerifier = useMemo(
    () => (saisie ? verifierValidation(nettoyer(saisie), textes) : []),
    [saisie, textes],
  );

  if (erreur && !saisie) return <Erreur message={erreur} />;
  if (!saisie) return <Chargement quoi="de l’annonce" />;

  const rh = utilisateur ? auMoins(utilisateur.role, 'rh') : false;
  const figee = !rh || detail?.statut === 'archivee';
  const familleVerrouillee = Boolean(detail && detail.statut !== 'brouillon');

  const changer = (partiel: Partial<SaisieAnnonce>) => {
    setSaisie({ ...saisie, ...partiel });
    setModifie(true);
  };
  const changerTexte = (langue: LangueAnnonce, valeur: string) => {
    setTextes((precedents) => ({ ...precedents, [langue]: valeur }));
    setModifie(true);
  };
  const majCritere = (index: number, partiel: Partial<CritereAnnonce>) =>
    changer({ criteres: saisie.criteres.map((c, i) => (i === index ? { ...c, ...partiel } : c)) });

  const executer = async (travail: () => Promise<void>) => {
    setErreur(null);
    setInfo(null);
    setEnvoi(true);
    try {
      await travail();
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Opération impossible');
    } finally {
      setEnvoi(false);
    }
  };

  const enregistrer = () =>
    executer(async () => {
      const corps: EnregistrementAnnonce = {
        saisie: nettoyer(saisie),
        textes: Object.fromEntries(saisie.langues.map((langue) => [langue, textes[langue] ?? ''])),
      };
      if (!detail) {
        const creee = await appel<AnnonceDetail>('/annonces', { method: 'POST', body: JSON.stringify(corps) });
        navigate(`/annonces/${creee.id}/edition`, { replace: true });
        return;
      }
      charger(await appel<AnnonceDetail>(`/annonces/${detail.id}`, { method: 'PUT', body: JSON.stringify(corps) }));
      setInfo('Enregistré.');
    });

  const valider = () => {
    if (!detail) return;
    const question = detail.reference
      ? 'La nouvelle version sera figée et remplacera le texte en vigueur. Valider ?'
      : 'La version sera figée et la référence attribuée. Valider ?';
    if (!window.confirm(question)) return;
    void executer(async () => {
      charger(await appel<AnnonceDetail>(`/annonces/${detail.id}/valider`, { method: 'POST' }));
      setInfo('Annonce validée : copier le texte vers les plateformes, puis déclarer chaque publication.');
    });
  };

  const cloturer = () => {
    if (!detail || !window.confirm('Clôturer l’annonce ? Les candidatures reçues restent consultables.')) return;
    void executer(async () => {
      charger(await appel<AnnonceDetail>(`/annonces/${detail.id}/cloturer`, { method: 'POST' }));
    });
  };

  const proposer = (langue: LangueAnnonce) => {
    if (textes[langue]?.trim() && !window.confirm('Remplacer le texte actuel par une nouvelle proposition ?')) {
      return;
    }
    const libelles = lieux.filter((l) => saisie.pointsDeVente.includes(l.id)).map((l) => l.libelle);
    changerTexte(langue, proposerTexte(nettoyer(saisie), { pointsDeVente: libelles }, langue));
  };

  const copier = (texte: string) => {
    navigator.clipboard
      .writeText(texte)
      .then(() => setInfo('Texte copié : le coller sur la plateforme.'))
      .catch(() => setErreur('Copie impossible : sélectionner le texte à la main.'));
  };

  const lireVersion = (numero: number) => {
    if (!detail) return;
    if (versionLue?.version === numero) {
      setVersionLue(null);
      return;
    }
    appel<TexteVersion>(`/annonces/${detail.id}/versions/${numero}`)
      .then(setVersionLue)
      .catch((e: Error) => setErreur(e.message));
  };

  return (
    <article className="edition-annonce">
      <div className="entete-annonce">
        <div>
          <h2>{detail ? detail.intitule : 'Nouvelle annonce'}</h2>
          {detail && (
            <p className="meta">
              <span className={`statut-annonce statut-${detail.statut}`}>
                {LIBELLES_STATUT_ANNONCE[detail.statut]}
              </span>
              {detail.reference && <code>{detail.reference}</code>}
              {detail.statut !== 'brouillon' && (
                <Link to={`/annonces/${detail.id}`}>{detail.candidatures} candidature(s)</Link>
              )}
            </p>
          )}
        </div>
      </div>

      {detail?.brouillonEnCours && detail.reference && (
        <p className="signal">
          Une nouvelle version est en préparation : le texte validé reste en vigueur jusqu’à sa validation.
        </p>
      )}

      <fieldset className="saisie" disabled={figee || envoi}>
        <legend>Poste</legend>
        <label className="champ">
          Métier
          <select
            value={saisie.familleId}
            disabled={familleVerrouillee}
            onChange={(e) => changer({ familleId: e.target.value })}
          >
            <option value="">Choisir…</option>
            {familles.map((f) => (
              <option key={f.id} value={f.id}>
                {f.libelle}
              </option>
            ))}
          </select>
          {familleVerrouillee && (
            <small className="mention">Fixée à la validation : la référence en dépend.</small>
          )}
        </label>
        <label className="champ">
          Intitulé
          <input
            value={saisie.intitule}
            maxLength={160}
            onChange={(e) => changer({ intitule: e.target.value })}
          />
        </label>

        <fieldset>
          <legend>Points de vente</legend>
          <div className="cases">
            {lieux.map((lieu) => (
              <label key={lieu.id} className="case">
                <input
                  type="checkbox"
                  checked={saisie.pointsDeVente.includes(lieu.id)}
                  onChange={(e) =>
                    changer({
                      pointsDeVente: e.target.checked
                        ? [...saisie.pointsDeVente, lieu.id]
                        : saisie.pointsDeVente.filter((p) => p !== lieu.id),
                    })
                  }
                />
                {lieu.libelle}
              </label>
            ))}
          </div>
        </fieldset>

        <div className="deux-colonnes">
          <label className="champ">
            Contrat
            <select
              value={saisie.typeContrat ?? ''}
              onChange={(e) => changer({ typeContrat: (e.target.value || null) as TypeContrat | null })}
            >
              <option value="">Non précisé</option>
              {TYPES_CONTRAT.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </label>
          <label className="champ">
            Prise de poste
            <input
              type="date"
              value={saisie.datePriseDePoste ?? ''}
              onChange={(e) => changer({ datePriseDePoste: e.target.value || null })}
            />
          </label>
          <label className="champ">
            Horaires
            <input
              value={saisie.horaires ?? ''}
              maxLength={300}
              placeholder="ex. travail posté, week-end par roulement"
              onChange={(e) => changer({ horaires: e.target.value })}
            />
          </label>
          <label className="champ">
            Rémunération
            <input
              value={saisie.remuneration ?? ''}
              maxLength={200}
              placeholder="facultatif"
              onChange={(e) => changer({ remuneration: e.target.value })}
            />
          </label>
        </div>

        <fieldset>
          <legend>Langues du texte</legend>
          <div className="cases">
            {LANGUES_ANNONCE.map((langue) => (
              <label key={langue} className="case">
                <input
                  type="checkbox"
                  checked={saisie.langues.includes(langue)}
                  disabled={saisie.langues.length === 1 && saisie.langues.includes(langue)}
                  onChange={(e) =>
                    changer({
                      langues: e.target.checked
                        ? LANGUES_ANNONCE.filter((l) => l === langue || saisie.langues.includes(l))
                        : saisie.langues.filter((l) => l !== langue),
                    })
                  }
                />
                {LIBELLES_LANGUE[langue]}
              </label>
            ))}
          </div>
        </fieldset>
      </fieldset>

      <fieldset className="saisie" disabled={figee || envoi}>
        <legend>Missions</legend>
        <ul className="liste-editable">
          {saisie.missions.map((mission, index) => (
            <li key={index}>
              <input
                value={mission}
                maxLength={300}
                aria-label={`Mission ${index + 1}`}
                onChange={(e) =>
                  changer({ missions: saisie.missions.map((m, i) => (i === index ? e.target.value : m)) })
                }
              />
              <button
                type="button"
                className="lien"
                onClick={() => changer({ missions: saisie.missions.filter((_, i) => i !== index) })}
              >
                Retirer
              </button>
            </li>
          ))}
        </ul>
        {saisie.missions.length < 15 && (
          <button
            type="button"
            className="bouton-second"
            onClick={() => changer({ missions: [...saisie.missions, ''] })}
          >
            Ajouter une mission
          </button>
        )}
      </fieldset>

      <fieldset className="saisie" disabled={figee || envoi}>
        <legend>Critères</legend>
        <p className="mention">
          Ils servent au tri automatique des CV. Jamais l’âge, la situation familiale, le sexe, la santé, la
          nationalité ou la religion.
        </p>
        <ul className="liste-editable criteres-annonce">
          {saisie.criteres.map((critere, index) => (
            <li key={index}>
              <select
                value={critere.type}
                aria-label="Type de critère"
                onChange={(e) => majCritere(index, { type: e.target.value as CritereAnnonce['type'] })}
              >
                <option value="eliminatoire">Éliminatoire</option>
                <option value="souhaitable">Souhaitable</option>
              </select>
              <select
                value={critere.axe}
                aria-label="Axe du barème"
                onChange={(e) => majCritere(index, { axe: e.target.value as Axe })}
              >
                {AXES.map((axe) => (
                  <option key={axe} value={axe}>
                    {LIBELLES_AXES[axe]}
                  </option>
                ))}
              </select>
              <input
                value={critere.libelle}
                maxLength={200}
                placeholder="ex. Expérience en vente alimentaire"
                aria-label="Libellé du critère"
                onChange={(e) => majCritere(index, { libelle: e.target.value })}
              />
              <button
                type="button"
                className="lien"
                onClick={() => changer({ criteres: saisie.criteres.filter((_, i) => i !== index) })}
              >
                Retirer
              </button>
            </li>
          ))}
        </ul>
        {saisie.criteres.length < 20 && (
          <button
            type="button"
            className="bouton-second"
            onClick={() =>
              changer({ criteres: [...saisie.criteres, { type: 'eliminatoire', axe: 'experience', libelle: '' }] })
            }
          >
            Ajouter un critère
          </button>
        )}
      </fieldset>

      <fieldset className="saisie" disabled={figee || envoi}>
        <legend>Texte de l’annonce</legend>
        {saisie.langues.map((langue) => (
          <div key={langue} className="texte-annonce">
            <div className="ligne-axe">
              <span>{LIBELLES_LANGUE[langue]}</span>
              <span className="actions-texte">
                <button type="button" className="lien" onClick={() => proposer(langue)}>
                  Proposer un texte
                </button>
                {textes[langue] && (
                  <button type="button" className="lien" onClick={() => copier(textes[langue] ?? '')}>
                    Copier
                  </button>
                )}
              </span>
            </div>
            <textarea
              rows={14}
              maxLength={8000}
              dir={langue === 'ar' ? 'rtl' : 'ltr'}
              lang={langue}
              aria-label={`Texte en ${LIBELLES_LANGUE[langue].toLowerCase()}`}
              value={textes[langue] ?? ''}
              onChange={(e) => changerTexte(langue, e.target.value)}
            />
          </div>
        ))}
        <p className="mention">
          {JETON_REFERENCE} est remplacé par la référence à la validation ; elle est ajoutée à la fin si le
          texte ne la contient plus.
        </p>
      </fieldset>

      {rh && !figee && aVerifier.length > 0 && (
        <div className="signal">
          <strong>Avant validation :</strong>
          <ul>
            {aVerifier.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        </div>
      )}
      {erreur && <Erreur message={erreur} />}
      {info && <p className="signal">{info}</p>}

      {rh && !figee && (
        <div className="actions">
          {detail?.statut === 'publiee' && (
            <button type="button" className="bouton-second" disabled={envoi} onClick={cloturer}>
              Clôturer l’annonce
            </button>
          )}
          <button
            type="button"
            className="bouton-second"
            disabled={envoi || !saisie.familleId || saisie.intitule.trim().length < 3}
            onClick={() => void enregistrer()}
          >
            Enregistrer
          </button>
          <button
            type="button"
            className="bouton"
            disabled={envoi || !detail?.brouillonEnCours || modifie || aVerifier.length > 0}
            title={modifie ? 'Enregistrer d’abord les modifications' : undefined}
            onClick={valider}
          >
            Valider
          </button>
        </div>
      )}

      {detail && detail.statut !== 'brouillon' && <Publications detail={detail} rh={rh} onChange={charger} />}

      {detail && detail.versions.length > 0 && (
        <section className="bloc">
          <h3>Versions</h3>
          <ul className="historique">
            {detail.versions.map((version) => (
              <li key={version.version}>
                <div className="ligne1">
                  <strong>Version {version.version}</strong>
                  <span className="mention">
                    {version.valideeLe
                      ? `validée le ${dateHeure(version.valideeLe)}${version.valideePar ? ` par ${version.valideePar}` : ''}`
                      : 'en préparation'}
                    {' · '}
                    {version.langues.map((l) => LIBELLES_LANGUE[l]).join(', ')}
                  </span>
                  {version.valideeLe && (
                    <button type="button" className="lien" onClick={() => lireVersion(version.version)}>
                      {versionLue?.version === version.version ? 'Masquer' : 'Lire'}
                    </button>
                  )}
                </div>
                {versionLue?.version === version.version &&
                  (Object.entries(versionLue.textes) as Array<[LangueAnnonce, string]>).map(([langue, texte]) => (
                    <pre key={langue} className="texte-extrait" dir={langue === 'ar' ? 'rtl' : 'ltr'} lang={langue}>
                      {texte}
                    </pre>
                  ))}
              </li>
            ))}
          </ul>
        </section>
      )}
    </article>
  );
}

const PUBLICATION_VIDE: Publication = {
  plateforme: 'tanitjobs',
  offreExterne: null,
  intitulePublie: null,
  url: null,
  publieeLe: null,
};

/** Publications déclarées : ce sont elles qui rattachent les notifications des plateformes. */
function Publications({
  detail,
  rh,
  onChange,
}: {
  detail: AnnonceDetail;
  rh: boolean;
  onChange: (detail: AnnonceDetail) => void;
}) {
  const [saisie, setSaisie] = useState<Publication>(PUBLICATION_VIDE);
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  const champ = (valeur: string | null) => (valeur?.trim() ? valeur.trim() : null);

  async function declarer(): Promise<void> {
    setErreur(null);
    setEnvoi(true);
    try {
      const corps: Publication = {
        ...saisie,
        offreExterne: champ(saisie.offreExterne),
        intitulePublie: champ(saisie.intitulePublie),
        url: champ(saisie.url),
      };
      onChange(
        await appel<AnnonceDetail>(`/annonces/${detail.id}/publications`, {
          method: 'POST',
          body: JSON.stringify(corps),
        }),
      );
      setSaisie(PUBLICATION_VIDE);
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Déclaration impossible');
    } finally {
      setEnvoi(false);
    }
  }

  return (
    <section className="bloc">
      <h3>Publications</h3>
      <p className="mention">
        Le numéro d’offre et l’intitulé publié rattachent les notifications des plateformes à cette annonce.
      </p>
      {detail.publications.length === 0 ? (
        <p className="mention">Aucune publication déclarée.</p>
      ) : (
        <ul className="historique">
          {detail.publications.map((publication) => (
            <li key={publication.id}>
              <div className="ligne1">
                <strong>{LIBELLES_PLATEFORME[publication.plateforme]}</strong>
                {publication.offreExterne && <code>n° {publication.offreExterne}</code>}
                {publication.publieeLe && (
                  <span className="mention">{publication.publieeLe.split('-').reverse().join('/')}</span>
                )}
              </div>
              {publication.intitulePublie && <p className="meta">{publication.intitulePublie}</p>}
              {publication.url && (
                <a href={publication.url} target="_blank" rel="noreferrer noopener">
                  {publication.url}
                </a>
              )}
            </li>
          ))}
        </ul>
      )}

      {rh && detail.statut !== 'archivee' && (
        <fieldset className="saisie" disabled={envoi}>
          <legend>Déclarer une publication</legend>
          <div className="deux-colonnes">
            <label className="champ">
              Plateforme
              <select
                value={saisie.plateforme}
                onChange={(e) => setSaisie({ ...saisie, plateforme: e.target.value as Plateforme })}
              >
                {PLATEFORMES.map((plateforme) => (
                  <option key={plateforme} value={plateforme}>
                    {LIBELLES_PLATEFORME[plateforme]}
                  </option>
                ))}
              </select>
            </label>
            <label className="champ">
              Numéro d’offre
              <input
                value={saisie.offreExterne ?? ''}
                maxLength={40}
                placeholder="ex. 133205"
                onChange={(e) => setSaisie({ ...saisie, offreExterne: e.target.value })}
              />
            </label>
            <label className="champ">
              Intitulé publié
              <input
                value={saisie.intitulePublie ?? ''}
                maxLength={200}
                placeholder="tel qu’il apparaît sur la plateforme"
                onChange={(e) => setSaisie({ ...saisie, intitulePublie: e.target.value })}
              />
            </label>
            <label className="champ">
              Date de publication
              <input
                type="date"
                value={saisie.publieeLe ?? ''}
                onChange={(e) => setSaisie({ ...saisie, publieeLe: e.target.value || null })}
              />
            </label>
          </div>
          <label className="champ">
            Adresse de l’offre
            <input
              type="url"
              value={saisie.url ?? ''}
              maxLength={500}
              placeholder="https://…"
              onChange={(e) => setSaisie({ ...saisie, url: e.target.value })}
            />
          </label>
          {erreur && <Erreur message={erreur} />}
          <div className="actions">
            <button type="button" className="bouton" onClick={() => void declarer()}>
              Déclarer
            </button>
          </div>
        </fieldset>
      )}
    </section>
  );
}
